import { html, css, type TemplateResult, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import sanitizeHtml from 'sanitize-html'

import { historyIcon } from './icons/historyIcon'
import type { SupportedLanguage, SupportedLocale } from '../i18n/i18n.no-deps'
import type { Answer } from '../domain/flows/types.no-deps'
import type { UserProfile } from '../domain/users/types.no-deps'
import { assumeExhaustion } from '../lib/typeHelpers.no-deps'
import type { Dictionary } from '../../shared/lib/functional/dictionary.no-deps'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'

@customElement('zaptic-answer-timeline')
export class AnswerTimeline extends LitElement {
    public static tagName = 'zaptic-answer-timeline'
    public events: [] = []

    static styles = css`
        zaptic-profile-circle {
            --ProfileCircle-size: 22px;
            --ProfileCircle-paddingRight: 5px;
            /* For some reason the circle doesn't line up right */
            margin-top: -2px;
        }

        .header {
            font-weight: var(--font-weight-medium);
            font-size: 16px;
            color: var(--grey3);
            display: flex;
            border: 0;
            padding: 0;
            background: none;
            padding-bottom: 14px;
            cursor: pointer;
        }
        .header > svg {
            padding-right: 8px;
        }
        .header:focus {
            outline: none;
            text-decoration: underline;
        }
        .answer {
            display: flex;
            font-weight: var(--font-weight-regular);
            font-size: 14px;
            color: var(--grey3);
            position: relative;
            padding-bottom: 14px;
        }

        .answer-value,
        .date,
        .user {
            font-weight: var(--font-weight-bold);
        }

        .line {
            width: 1px;
            background-color: var(--grey6);
            position: absolute;
            left: 11px;
            top: 21px;
            height: calc(100% - 24px);
        }

        .answer:last-child,
        .answer:only-child {
            padding-bottom: 0;
        }

        .answer:last-child > .line,
        .answer:only-child > .line {
            display: none;
        }

        .details-hidden {
            display: none;
        }
    `

    @property({ converter: (value) => JSON.parse(value) })
    answers: Answer[]

    @property({ converter: (value) => JSON.parse(value) })
    'choices-lookup': Dictionary<string> | null

    @property({ converter: (value) => JSON.parse(value) })
    'guide-names-lookup': Dictionary<string> | null

    @property({ converter: (value) => JSON.parse(value) })
    language: SupportedLanguage

    @property({ converter: (value) => JSON.parse(value) })
    locale: SupportedLocale

    @property({ converter: (value) => JSON.parse(value) })
    users: UserProfile[]

    @property({ converter: (value) => JSON.parse(value) })
    bare = false

    @property({ converter: (value) => JSON.parse(value) })
    expanded: boolean = false

    private toggleDetails() {
        this.expanded = !this.expanded
    }

    render() {
        // date values don't get deserialised properly
        const answersWithDates = this.answers?.map((answer) => ({ ...answer, timestamp: new Date(answer.timestamp) }))

        const sortedAnswers = answersWithDates
            ?.sort((a, b) => +new Date(a.timestamp) - +new Date(b.timestamp))
            .map((answer, idx) =>
                answerHtml(
                    answer,
                    this['choices-lookup'],
                    this['guide-names-lookup'],
                    idx === 0,
                    this.language,
                    this.locale,
                    this.users
                )
            )

        return html`
            ${this.bare
                ? ''
                : html`
                      <button class="header" @click=${this.toggleDetails}>
                          ${historyIcon}
                          ${this.expanded
                              ? window.i18n('shared:answerTimeline.expanded')
                              : window.i18n('shared:answerTimeline.notExpanded')}
                      </button>
                  `}
            <div class=${this.expanded || this.bare ? '' : 'details-hidden'}>${sortedAnswers}</div>
        `
    }
}

function answerHtml(
    answer: Answer,
    choicesLookup: Dictionary<string> | null,
    guideNamesLookup: Dictionary<string> | null,
    first: boolean,
    language: SupportedLanguage,
    locale: SupportedLocale,
    users: UserProfile[]
) {
    const user = users.find((usr) => usr.userId === answer.user)
    const date = answer.timestamp.toLocaleString(locale, {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    })
    const content = contentString(answer, choicesLookup, guideNamesLookup, language, first)
    const answerHtml = window.i18n('shared:answerTimeline.answerHtml', {
        userName: user.name,
        content: renderAsString(content),
        date,
        espan: '</span>',
        userSpan: '<span class="user">',
        dateSpan: '<span class="date">',
    })

    return (
        content &&
        html`
            <span class="answer">
                <div class="line"></div>
                <zaptic-profile-circle
                    name=${user.name}
                    color=${user.profileCircleColor}
                    profile-image-url=${user.profileImageUrl}
                ></zaptic-profile-circle>
                <span> ${unsafeHTML(answerHtml)} </span>
            </span>
        `
    )
}

function contentString(
    answer: Answer,
    choicesLookup: Dictionary<string> | null,
    guideNamesLookup: Dictionary<string> | null,
    _: SupportedLanguage,
    first: boolean
): TemplateResult {
    switch (answer.type) {
        case 'confirmation': {
            return html`${answer.value
                ? window.i18n('shared:answerTimeline.confirmation.answerY')
                : window.i18n('shared:answerTimeline.confirmation.answerN')}`
        }

        case 'yes-no': {
            const value = answer.value === 'yes' ? window.i18n('shared:common.yes') : window.i18n('shared:common.no')

            return html`${first
                    ? window.i18n('shared:answerTimeline.yesNo.first')
                    : window.i18n('shared:answerTimeline.yesNo.notFirst')}
                <span class="answer-value">"${value}"</span> `
        }

        case 'number': {
            const value = answer.value.toLocaleString()

            return html`${first
                    ? window.i18n('shared:answerTimeline.number.first')
                    : window.i18n('shared:answerTimeline.number.notFirst')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
        }

        case 'radio': {
            const idString = answer.value.__brand === 'FlowChoiceId' ? answer.value.toString() : answer.value
            const value =
                choicesLookup !== null && choicesLookup[answer.value] !== undefined
                    ? choicesLookup[answer.value]
                    : idString

            return html`${first
                    ? window.i18n('shared:answerTimeline.radio.first')
                    : window.i18n('shared:answerTimeline.radio.notFirst')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
        }

        case 'checkbox': {
            const value =
                choicesLookup === null
                    ? answer.value.join(', ')
                    : answer.value.map((a) => (choicesLookup[a] !== undefined ? choicesLookup[a] : a)).join(', ')

            return html`${first
                    ? window.i18n('shared:answerTimeline.checkbox.first')
                    : window.i18n('shared:answerTimeline.checkbox.notFirst')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
        }

        case 'image-select': {
            const value =
                choicesLookup === null
                    ? answer.value.join(', ')
                    : answer.value.map((a) => (choicesLookup[a] !== undefined ? choicesLookup[a] : a)).join(', ')

            return html`${first
                    ? window.i18n('shared:answerTimeline.imageSelect.first')
                    : window.i18n('shared:answerTimeline.imageSelect.notFirst')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
        }

        case 'photo': {
            const value = answer.value.length

            return html`${window.i18n('shared:answerTimeline.photo', {
                answer: value,
                span: '<span class="answer-value">',
                espan: '</span>',
            })}`
        }

        case 'text-capture': {
            const value = answer.value

            return html`${first
                    ? window.i18n('shared:answerTimeline.textCapture.first')
                    : window.i18n('shared:answerTimeline.textCapture.notFirst')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
        }

        case 'section': {
            return html`${window.i18n('shared:answerTimeline.section')}`
        }

        case 'datetime': {
            switch (answer.value.type) {
                case 'date': {
                    const date = new Date(answer.value.date.year, answer.value.date.month, answer.value.date.day)
                    const value = date.toLocaleDateString()

                    return html`${first
                            ? window.i18n('shared:answerTimeline.dateTime.date.first')
                            : window.i18n('shared:answerTimeline.dateTime.date.notFirst')}
                        <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
                }
                case 'time': {
                    const time = new Date(0, 0, 0, answer.value.time.hour, answer.value.time.minute)
                    const value = time.toLocaleTimeString()

                    return html`${first
                            ? window.i18n('shared:answerTimeline.dateTime.time.first')
                            : window.i18n('shared:answerTimeline.dateTime.time.notFirst')}
                        <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
                }
                case 'datetime': {
                    const date = new Date(
                        answer.value.date.year,
                        answer.value.date.month,
                        answer.value.date.day,
                        answer.value.time.hour,
                        answer.value.time.minute
                    )
                    const value = date.toLocaleString()

                    return html`${first
                            ? window.i18n('shared:answerTimeline.dateTime.dateTime.first')
                            : window.i18n('shared:answerTimeline.dateTime.dateTime.notFirst')}
                        <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span> `
                }
                default:
                    assumeExhaustion(answer.value)
            }
        }

        case 'signature': {
            return html`${first
                ? window.i18n('shared:answerTimeline.signature.first')
                : window.i18n('shared:answerTimeline.signature.notFirst')}`
        }

        case 'dropdown': {
            const value =
                choicesLookup === null
                    ? answer.value.join(', ')
                    : answer.value.map((a) => (choicesLookup[a] !== undefined ? choicesLookup[a] : a)).join(', ')

            return html`${first
                    ? window.i18n('shared:answerTimeline.dropdown.first')
                    : window.i18n('shared:answerTimeline.dropdown.notFirst')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span>`
        }

        case 'extensible-files': {
            const value = answer.value.length

            return html`${window.i18n('shared:answerTimeline.extensibleFiles', {
                answer: `${value}`,
                span: '<span class="answer-value">',
                espan: '</span>',
            })}`
        }

        case 'files-select': {
            const value = answer.value.length

            return html`${window.i18n('shared:answerTimeline.filesSelect', {
                answer: `${value}`,
                span: '<span class="answer-value">',
                espan: '</span>',
            })}`
        }

        case 'unit-select': {
            if (answer.value === 'no-units-available') {
                return html`${window.i18n('shared:answerTimeline.unitSelect.noUnits')}`
            }

            let value = answer.value
                .map((unitId) => (choicesLookup && choicesLookup[unitId.toString()]) || unitId)
                .join(', ')

            // the user didn't pick any unit from the list
            if (value === '') {
                value = window.i18n('shared:answerTimeline.unitSelect.disableUnitsFilter')
            }

            return html`${window.i18n('shared:answerTimeline.unitSelect.units')}
                <span class="answer-value">"${sanitizeHtml(value, { allowedTags: [] })}"</span>`
        }

        case 'reports': {
            // TODO
            return null
        }

        case 'guide': {
            if (answer.automaticallyGenerated) {
                return null
            }
            if (!guideNamesLookup) {
                return null
            }

            const value = Array.isArray(answer.value)
                ? answer.value.map((v) => guideNamesLookup[v.guideId]).join(', ')
                : guideNamesLookup[answer.value.guideId]
            return html`${window.i18n('shared:answerTimeline.guide', {
                guideName: `${value}`,
                span: '<span class="answer-value">',
                espan: '</span>',
            })}`
        }

        default:
            assumeExhaustion(answer)
    }
}

// https://stackoverflow.com/a/70658580
function renderAsString(data: TemplateResult) {
    const { strings, values } = data
    const v = [...values, ''] // + last empty part
    return strings.reduce((acc, s, i) => acc + s + v[i], '')
}
